'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Eye, EyeOff, Lock, Mail, AlertCircle, ArrowRight, ShieldCheck, ArrowLeft, User, Phone, BookOpen, Users, CheckCircle2, Calendar } from 'lucide-react';

const programmes = [
  'Strategic Management and Leadership Development Level 6',
  'Human Resource Management Level 5',
  'Human Resource Management Level 4',
  'Logistics Management Level 5',
  'Business Administration Level 5',
  'Business Administration Management Level 3',
  'Supervisory Management Level 3',
  'Small Business Management Level 4',
  'Training and Development Level 4',
  'TVET Instruction/Delivery Level 5',
  'Assessment Level 4',
  'Digital Marketing Management Level 4',
  'Early Childhood Development Level 4',
  'Construction Project Management Level 5',
];

const intakePeriods = ['April Intake', 'September Intake'];

const cohorts = [
  'Cohort 2026-A',
  'Cohort 2026-B',
  'Cohort 2026-C',
  'Cohort 2025-B (Legacy)',
];

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  programme: string;
  intakePeriod: string;
  cohort: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

export default function StudentRegisterClient() {
  const [form, setForm] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    programme: '',
    intakePeriod: '',
    cohort: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!form.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) newErrors.email = 'Valid email is required';
    if (!form.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!form.programme) newErrors.programme = 'Please select a programme';
    if (!form.intakePeriod) newErrors.intakePeriod = 'Please select an intake period';
    if (!form.cohort) newErrors.cohort = 'Please select a cohort';
    if (!form.password || form.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (!form.agreeTerms) newErrors.agreeTerms = 'You must agree to the terms';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();

      // Create the Supabase auth account
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        options: {
          data: {
            full_name: form.fullName.trim(),
            role: 'student',
            phone: form.phone.trim(),
            programme: form.programme,
            intake_period: form.intakePeriod,
            cohort: form.cohort,
          },
        },
      });

      if (signUpError) {
        if (signUpError.message.toLowerCase().includes('already registered') || signUpError.message.toLowerCase().includes('already exists')) {
          setErrors({ email: 'An account with this email already exists. Please sign in instead.' });
        } else {
          setErrors({ email: signUpError.message });
        }
        setLoading(false);
        return;
      }

      if (data?.user) {
        // Use server-side API to create user_profiles (bypasses RLS)
        try {
          await fetch('/api/auth/create-profile', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: data.user.id,
              email: form.email.trim().toLowerCase(),
              fullName: form.fullName.trim(),
              phone: form.phone.trim(),
              programme: form.programme,
              intakePeriod: form.intakePeriod,
              cohort: form.cohort,
            }),
          });
        } catch (profileErr) {
          // Non-fatal: profile can be created later; auth account is created
          console.warn('Profile creation via API failed, attempting direct upsert:', profileErr);
          await supabase.from('user_profiles').upsert({
            id: data.user.id,
            email: form.email.trim().toLowerCase(),
            full_name: form.fullName.trim(),
            role: 'student',
            phone: form.phone.trim(),
          }, { onConflict: 'id' });
        }
      }

      setLoading(false);
      setSuccess(true);
    } catch (err) {
      setErrors({ email: 'An unexpected error occurred. Please try again.' });
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center animate-fade-in">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-2xl overflow-hidden border-2 border-primary/20 shadow-lg">
              <Image
                src="/assets/images/logo_1_1_-1778028950193.jpeg"
                alt="Ziva Niara Solutions logo"
                width={80}
                height={80}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} className="text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-3">Account Created!</h1>
          <p className="text-muted-foreground text-sm mb-2">
            Welcome to Ziva DocuSecure, <strong>{form.fullName}</strong>.
          </p>
          <p className="text-muted-foreground text-sm mb-8">
            Your account has been created for <strong>{form.programme}</strong>. Please complete your biodata registration form to proceed.
          </p>
          <div className="space-y-3">
            <Link href="/student-registration-form" className="btn-primary w-full py-3 flex items-center justify-center gap-2">
              Complete Biodata Form <ArrowRight size={15} />
            </Link>
            <Link href="/student-sign-in" className="btn-outline w-full py-3">
              Sign In to Your Account
            </Link>
          </div>
          <div className="mt-6 p-4 bg-secondary/50 border border-primary/20 rounded-xl text-left">
            <p className="text-xs font-semibold text-primary mb-2">Registration Summary</p>
            <div className="space-y-1.5">
              {[
                { label: 'Name', value: form.fullName },
                { label: 'Email', value: form.email },
                { label: 'Programme', value: form.programme },
                { label: 'Intake', value: form.intakePeriod },
                { label: 'Cohort', value: form.cohort },
              ].map((item) => (
                <div key={item.label} className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{item.label}:</span>
                  <span className="font-medium text-foreground text-right max-w-[60%]">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-6">
            Need help? Contact us at{' '}
            <a href="mailto:docusecure@myzivasol.com" className="text-primary font-semibold hover:underline">docusecure@myzivasol.com</a>
            {' '}or call{' '}
            <a href="tel:+18768650444" className="text-primary font-semibold hover:underline">1-876-865-0444</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-[420px] xl:w-[480px] flex-shrink-0 bg-primary p-10 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-white/5" />
          <div className="absolute top-1/2 -left-16 w-48 h-48 rounded-full bg-white/5" />
          <div className="absolute -bottom-16 right-10 w-64 h-64 rounded-full bg-accent/10" />
        </div>
        <div className="relative z-10">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/20 border border-white/30 flex-shrink-0">
              <Image
                src="/assets/images/logo_1_1_-1778028950193.jpeg"
                alt="Ziva Niara Solutions logo"
                width={56}
                height={56}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <span className="block text-lg font-bold text-white">Ziva DocuSecure</span>
              <span className="block text-xs text-white/60">Ziva Niara Solutions</span>
            </div>
          </div>
          <div className="mb-8">
            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-5">
              <Users size={28} className="text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white leading-tight mb-3">Student Registration</h2>
            <p className="text-white/70 text-sm leading-relaxed">
              Create your Ziva DocuSecure account to begin your programme registration. After registering, you will be directed to complete your biodata form.
            </p>
          </div>
          <div className="space-y-3">
            {[
              'Create your secure student account',
              'Complete your full biodata form',
              'Read and accept the consent declaration',
              'Sign electronically',
              'Upload required documents',
              'Track your submission status',
            ].map((f, i) => (
              <div key={f} className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-bold text-white">{i + 1}</span>
                </div>
                <span className="text-sm text-white/80">{f}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10">
          <div className="h-px bg-white/20 mb-4" />
          <p className="text-xs text-white/50">docusecure.myzivasol.com</p>
          <p className="text-xs text-white/40 mt-1">© 2026 Ziva Niara Solutions. Suite 16, 4-5 Lismore Avenue, Kingston, St. Andrew, Jamaica</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-start p-6 sm:p-10 overflow-y-auto">
        <div className="w-full max-w-md">
          <Link href="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">
            <ArrowLeft size={14} /> Back to Home
          </Link>

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl overflow-hidden border border-primary/20">
              <Image
                src="/assets/images/logo_1_1_-1778028950193.jpeg"
                alt="Ziva Niara Solutions logo"
                width={48}
                height={48}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <span className="block text-sm font-bold text-primary">Ziva DocuSecure</span>
              <span className="block text-[10px] text-muted-foreground">Ziva Niara Solutions</span>
            </div>
          </div>

          <div className="mb-7">
            <h1 className="text-2xl font-bold text-foreground">Create Student Account</h1>
            <p className="text-sm text-muted-foreground mt-1">Register to begin your programme application</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="form-label">Full Name <span className="text-red-500">*</span></label>
              <div className="relative">
                <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={form.fullName}
                  onChange={(e) => handleChange('fullName', e.target.value)}
                  className="form-input pl-9"
                  placeholder="Enter your full name"
                />
              </div>
              {errors.fullName && <p className="form-error"><AlertCircle size={11} />{errors.fullName}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="form-label">Email Address <span className="text-red-500">*</span></label>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="form-input pl-9"
                  placeholder="your@email.com"
                />
              </div>
              {errors.email && <p className="form-error"><AlertCircle size={11} />{errors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="form-label">Phone Number <span className="text-red-500">*</span></label>
              <div className="relative">
                <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="form-input pl-9"
                  placeholder="+1 (876) 000-0000"
                />
              </div>
              {errors.phone && <p className="form-error"><AlertCircle size={11} />{errors.phone}</p>}
            </div>

            {/* Programme */}
            <div>
              <label className="form-label">Programme Applied For <span className="text-red-500">*</span></label>
              <div className="relative">
                <BookOpen size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <select
                  value={form.programme}
                  onChange={(e) => handleChange('programme', e.target.value)}
                  className="form-input pl-9 appearance-none cursor-pointer"
                >
                  <option value="">Select programme...</option>
                  {programmes.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              {errors.programme && <p className="form-error"><AlertCircle size={11} />{errors.programme}</p>}
            </div>

            {/* Intake Period */}
            <div>
              <label className="form-label">Intake Period <span className="text-red-500">*</span></label>
              <div className="relative">
                <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <select
                  value={form.intakePeriod}
                  onChange={(e) => handleChange('intakePeriod', e.target.value)}
                  className="form-input pl-9 appearance-none cursor-pointer"
                >
                  <option value="">Select intake period...</option>
                  {intakePeriods.map((ip) => (
                    <option key={ip} value={ip}>{ip}</option>
                  ))}
                </select>
              </div>
              {errors.intakePeriod && <p className="form-error"><AlertCircle size={11} />{errors.intakePeriod}</p>}
            </div>

            {/* Cohort */}
            <div>
              <label className="form-label">Cohort / Class Name <span className="text-red-500">*</span></label>
              <select
                value={form.cohort}
                onChange={(e) => handleChange('cohort', e.target.value)}
                className="form-input appearance-none cursor-pointer"
              >
                <option value="">Select cohort...</option>
                {cohorts.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {errors.cohort && <p className="form-error"><AlertCircle size={11} />{errors.cohort}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="form-label">Password <span className="text-red-500">*</span></label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  className="form-input pl-9 pr-10"
                  placeholder="Create a secure password (min. 8 characters)"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {errors.password && <p className="form-error"><AlertCircle size={11} />{errors.password}</p>}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="form-label">Confirm Password <span className="text-red-500">*</span></label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  className="form-input pl-9 pr-10"
                  placeholder="Repeat your password"
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {errors.confirmPassword && <p className="form-error"><AlertCircle size={11} />{errors.confirmPassword}</p>}
            </div>

            {/* Terms */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.agreeTerms}
                onChange={(e) => handleChange('agreeTerms', e.target.checked)}
                className="w-3.5 h-3.5 rounded accent-primary cursor-pointer mt-0.5 flex-shrink-0"
              />
              <span className="text-xs text-muted-foreground leading-relaxed">
                I agree to the{' '}
                <button type="button" className="text-primary font-semibold hover:underline">Terms of Service</button>
                {' '}and{' '}
                <button type="button" className="text-primary font-semibold hover:underline">Privacy Policy</button>
                {' '}of Ziva Niara Solutions.
              </span>
            </label>
            {errors.agreeTerms && <p className="form-error"><AlertCircle size={11} />{errors.agreeTerms}</p>}

            <button type="submit" disabled={loading} className="btn-primary w-full py-2.5">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Creating account...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Create Account & Continue <ArrowRight size={15} />
                </span>
              )}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-5">
            Already registered?{' '}
            <Link href="/student-sign-in" className="text-primary font-semibold hover:underline">Sign in</Link>
          </p>

          <div className="mt-5 flex items-start gap-2 p-3 bg-secondary/40 border border-primary/15 rounded-xl">
            <ShieldCheck size={14} className="text-primary flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Your personal data is protected. Ziva Niara Solutions collects information solely for training registration, verification, and certification purposes.
            </p>
          </div>

          <div className="mt-4 text-center text-[11px] text-muted-foreground space-y-0.5">
            <p>Need help? <a href="mailto:docusecure@myzivasol.com" className="text-primary hover:underline">docusecure@myzivasol.com</a></p>
            <p>Support: <a href="tel:+18768650444" className="text-primary hover:underline">1-876-865-0444</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}
